import React, { useState, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  Tabs,
  Tab,
  Stack,
} from "@mui/material";
import { Scaffold } from "../Scaffold";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { State } from "../redux/types";
import { 
  TabPanel, 
  a11yProps, 
  UserSidebar, 
  AccountInfoTab, 
  ActionButtons 
} from "../components/MasterProfile";
import { userDetails } from "../api/api";
import { setResult } from "../redux/actions";
import LoadingSpinner from "../LoadingSpinner";

export const MasterProfile = () => {
  const [value, setValue] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const result = useSelector((state: State) => state.result);
  const customer = result?.customer;
  const vendorResults = result?.vendorResultsList;
  const zaxbysId = customer?.zaxbysId
    ?? vendorResults?.find((v: any) => v.vendorKey === "ZaxbysAdmin")?.customerVendorId;

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // Refresh customer data after successful action with retry to handle eventual consistency
  const handleActionSuccess = useCallback(async (actionKey?: string) => {
    if (actionKey === "privacyDelete") {
      dispatch(setResult(null as any));
      setTimeout(() => {
        navigate("/dashboard");
      }, 1500);
      return;
    }
    
    const mpId = customer?.mpId ?? customer?.mpID;
    if (!mpId) {
      return;
    }
    
    setIsRefreshing(true);
    try {
      await new Promise<void>(resolve => { setTimeout(resolve, 2000); });
      const response = await userDetails(mpId) as any;

      if (response?.customer || response?.value) {
        dispatch(setResult({ ...response, _ts: Date.now() }));
        globalThis.localStorage.setItem("profile", JSON.stringify(response));
      }
    } catch (error) {
      console.debug("Master profile refetch failed", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [customer?.mpId, customer?.mpID, dispatch, navigate]);

  if (!customer) {
    return (
      <Scaffold navBar>
        <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <LoadingSpinner />
        </Box>
      </Scaffold>
    );
  }

  return (
    <Scaffold navBar>
      {isRefreshing && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            bgcolor: "rgba(255,255,255,0.7)",
          }}
        >
          <LoadingSpinner />
        </Box>
      )}
      <Typography variant="h4" sx={{ mb: 2, ml: 2 }}>Master Profile</Typography>
      <Box sx={{ width: "100%", maxWidth: 1400, mx: "auto", p: 2 }}>
        <Stack direction="row" spacing={3} alignItems="stretch">
          {/* Left Sidebar - User Info */}
          <UserSidebar
            customer={customer}
            vendorResults={vendorResults || []}
            flaggedForDeletion={result?.flaggedForDeletion === true}
          />

          {/* Right Panel - Tabs, Content, and Action Buttons */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Card sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={value}
                  onChange={handleChange}
                  aria-label="master profile tabs"
                  sx={{ 
                    backgroundColor: "#041E42",
                    "& .MuiTab-root": {
                      color: "white",
                      fontWeight: 600,
                      "&.Mui-selected": {
                        color: "white",
                        backgroundColor: "rgba(255,255,255,0.1)"
                      }
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: "white"
                    }
                  }}
                >
                  <Tab label="Account Info" {...a11yProps(0)} />
                  <Tab label="Order History" {...a11yProps(1)} />
                  <Tab label="Customer History" {...a11yProps(2)} />
                  <Tab label="Subscription History" {...a11yProps(3)} />
                </Tabs>
              </Box>

              <Box sx={{ flex: 1 }}>
                <TabPanel value={value} index={0}>
                  <AccountInfoTab customer={customer} />
                </TabPanel>

                <TabPanel value={value} index={1}>
                  <Typography variant="h6" gutterBottom>
                    Order History Coming Soon...
                  </Typography>
                </TabPanel>

                <TabPanel value={value} index={2}>
                  <Typography variant="h6" gutterBottom>
                    Customer History Coming Soon...
                  </Typography>
                </TabPanel>

                <TabPanel value={value} index={3}>
                  <Typography variant="h6" gutterBottom>
                    Subscription History Coming Soon...
                  </Typography>
                </TabPanel>
              </Box>

              <ActionButtons 
                mpId={customer?.mpId ?? customer?.mpID}
                cognitoId={customer?.cognitoId}
                zaxbysId={zaxbysId}
                userDetails={{
                  firstName: customer?.firstName,
                  lastName: customer?.lastName,
                  email: customer?.email,
                  phoneNumber: customer?.mobilePhoneNumber,
                  dateOfBirth: customer?.birthDate,
                }}
                customerName={`${customer?.firstName ?? ""}_${customer?.lastName ?? ""}`}
                isAccountEnabled={customer?.cognitoEnabled === "true" || customer?.cognitoEnabled === true}
                gpcSignal={customer?.privacySettings?.gpcSignal === true}
                isLocked={customer?.locked === true}
                onActionSuccess={handleActionSuccess}
              />
            </Card>
          </Box>
        </Stack>
      </Box>
    </Scaffold>
  );
};
